// TEST-EXEMPT: Visual/animation-only component with no testable logic
import React, { useRef, useEffect } from 'react'
import { Renderer, Program, Mesh, Triangle } from 'ogl'

interface LiquidChromeProps extends React.HTMLAttributes<HTMLDivElement> {
  baseColor?: [number, number, number]
  speed?: number
  amplitude?: number
  frequencyX?: number
  frequencyY?: number
  interactive?: boolean
}

/* ── Mobile detection (cached once at module level) ── */
const IS_MOBILE = typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches

/* Scale factor for mobile: render at half-resolution to reduce GPU fill rate */
const MOBILE_SCALE = 0.5

export const LiquidChrome: React.FC<LiquidChromeProps> = ({
  baseColor = [0.1, 0.1, 0.1],
  speed = 0.2,
  amplitude = 0.5,
  frequencyX = 3,
  frequencyY = 2,
  interactive = true,
  ...props
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current

    let renderer: Renderer
    try {
      renderer = new Renderer({ antialias: !IS_MOBILE })
    } catch (_e) {
      console.warn('[LiquidChrome] WebGL unavailable — skipping render')
      return
    }

    const gl = renderer.gl
    if (!gl) {
      console.warn('[LiquidChrome] WebGL context is null — skipping render')
      return
    }

    gl.clearColor(1, 1, 1, 1)

    const vertexShader = `
      attribute vec2 position;
      attribute vec2 uv;
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = vec4(position, 0.0, 1.0);
      }
    `

    /* ── Fragment shader ──
       Desktop: 3×3 supersampled (9 samples) for smooth edges
       Mobile:  single sample for dramatically reduced GPU cost */
    const fragmentShader = IS_MOBILE
      ? `
      precision mediump float;
      uniform float uTime;
      uniform vec3 uResolution;
      uniform vec3 uBaseColor;
      uniform float uAmplitude;
      uniform float uFrequencyX;
      uniform float uFrequencyY;
      uniform vec2 uMouse;
      varying vec2 vUv;

      void main() {
          vec2 fragCoord = vUv * uResolution.xy;
          vec2 uv = (2.0 * fragCoord - uResolution.xy) / min(uResolution.x, uResolution.y);

          for (float i = 1.0; i < 10.0; i++){
              uv.x += uAmplitude / i * cos(i * uFrequencyX * uv.y + uTime + uMouse.x * 3.14159);
              uv.y += uAmplitude / i * cos(i * uFrequencyY * uv.x + uTime + uMouse.y * 3.14159);
          }

          vec3 color = uBaseColor / abs(sin(uTime - uv.y - uv.x));
          gl_FragColor = vec4(color, 1.0);
      }
    `
      : `
      precision highp float;
      uniform float uTime;
      uniform vec3 uResolution;
      uniform vec3 uBaseColor;
      uniform float uAmplitude;
      uniform float uFrequencyX;
      uniform float uFrequencyY;
      uniform vec2 uMouse;
      varying vec2 vUv;

      vec4 renderImage(vec2 uvCoord) {
          vec2 fragCoord = uvCoord * uResolution.xy;
          vec2 uv = (2.0 * fragCoord - uResolution.xy) / min(uResolution.x, uResolution.y);

          for (float i = 1.0; i < 10.0; i++){
              uv.x += uAmplitude / i * cos(i * uFrequencyX * uv.y + uTime + uMouse.x * 3.14159);
              uv.y += uAmplitude / i * cos(i * uFrequencyY * uv.x + uTime + uMouse.y * 3.14159);
          }

          vec2 diff = (uvCoord - uMouse);
          float dist = length(diff);
          float falloff = exp(-dist * 20.0);
          float ripple = sin(10.0 * dist - uTime * 2.0) * 0.03;
          uv += (diff / (dist + 0.0001)) * ripple * falloff;

          vec3 color = uBaseColor / abs(sin(uTime - uv.y - uv.x));
          return vec4(color, 1.0);
      }

      void main() {
          vec4 col = vec4(0.0);
          int samples = 0;
          for (int i = -1; i <= 1; i++){
              for (int j = -1; j <= 1; j++){
                  vec2 offset = vec2(float(i), float(j)) * (1.0 / min(uResolution.x, uResolution.y));
                  col += renderImage(vUv + offset);
                  samples++;
              }
          }
          gl_FragColor = col / float(samples);
      }
    `

    const geometry = new Triangle(gl)
    const program = new Program(gl, {
      vertex: vertexShader,
      fragment: fragmentShader,
      uniforms: {
        uTime: { value: 0 },
        uResolution: {
          value: new Float32Array([
            gl.canvas.width,
            gl.canvas.height,
            gl.canvas.width / gl.canvas.height,
          ]),
        },
        uBaseColor: { value: new Float32Array(baseColor) },
        uAmplitude: { value: amplitude },
        uFrequencyX: { value: frequencyX },
        uFrequencyY: { value: frequencyY },
        uMouse: { value: new Float32Array([0, 0]) },
      },
    })
    const mesh = new Mesh(gl, { geometry, program })

    function resize() {
      const scale = IS_MOBILE ? MOBILE_SCALE : 1
      renderer.setSize(container.offsetWidth * scale, container.offsetHeight * scale)
      /* Keep canvas CSS size at 100% even though internal resolution is lower */
      gl.canvas.style.width = '100%'
      gl.canvas.style.height = '100%'
      const resUniform = program.uniforms.uResolution.value as Float32Array
      resUniform[0] = gl.canvas.width
      resUniform[1] = gl.canvas.height
      resUniform[2] = gl.canvas.width / gl.canvas.height
    }
    window.addEventListener('resize', resize)
    resize()

    function handleMouseMove(event: MouseEvent) {
      const rect = container.getBoundingClientRect()
      const x = (event.clientX - rect.left) / rect.width
      const y = 1 - (event.clientY - rect.top) / rect.height
      const mouseUniform = program.uniforms.uMouse.value as Float32Array
      mouseUniform[0] = x
      mouseUniform[1] = y
    }

    function handleTouchMove(event: TouchEvent) {
      if (event.touches.length > 0) {
        const touch = event.touches[0]
        const rect = container.getBoundingClientRect()
        const x = (touch.clientX - rect.left) / rect.width
        const y = 1 - (touch.clientY - rect.top) / rect.height
        const mouseUniform = program.uniforms.uMouse.value as Float32Array
        mouseUniform[0] = x
        mouseUniform[1] = y
      }
    }

    if (interactive) {
      container.addEventListener('mousemove', handleMouseMove)
      container.addEventListener('touchmove', handleTouchMove, { passive: true })
    }

    /* ── Visibility-gated animation loop ──
       Uses IntersectionObserver to pause the rAF loop when the canvas
       is scrolled off-screen, saving CPU/GPU cycles. */
    let animationId: number
    let isVisible = true

    function update(t: number) {
      if (isVisible) {
        program.uniforms.uTime.value = t * 0.001 * speed
        renderer.render({ scene: mesh })
      }
      animationId = requestAnimationFrame(update)
    }
    animationId = requestAnimationFrame(update)

    /* Observe visibility — pause rendering when offscreen */
    let observer: IntersectionObserver | null = null
    if (typeof IntersectionObserver !== 'undefined') {
      observer = new IntersectionObserver(
        ([entry]) => {
          isVisible = entry.isIntersecting
        },
        { threshold: 0 }
      )
      observer.observe(container)
    }

    container.appendChild(gl.canvas)

    return () => {
      cancelAnimationFrame(animationId)
      observer?.disconnect()
      window.removeEventListener('resize', resize)
      if (interactive) {
        container.removeEventListener('mousemove', handleMouseMove)
        container.removeEventListener('touchmove', handleTouchMove)
      }
      if (gl.canvas.parentElement) {
        gl.canvas.parentElement.removeChild(gl.canvas)
      }
      gl.getExtension('WEBGL_lose_context')?.loseContext()
    }
  }, [baseColor, speed, amplitude, frequencyX, frequencyY, interactive])

  return <div ref={containerRef} className="w-full h-full" {...props} />
}

export { LiquidChrome as default }
